import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingPage from '../LoadingPage';
import { UserService } from '../../services/api';
import type { User } from '../../types';

/**
 * Auto-dismiss delay for the success Alert (issue #1948).
 *
 * Matches the cadence used by `ProfilePanel` and `DigestFrequencyPanel` so
 * the Settings page maintains a single, consistent feedback rhythm across
 * panels.
 */
const SUCCESS_AUTODISMISS_MS = 3000;

/** Default state for the three email-subscription toggles when the user record has none persisted. */
const DEFAULT_PREFERENCES = {
    ackDueReminders: true,
    policyAssignments: true,
    weeklyAdminDigest: true,
} as const;

/**
 * Settings → Account → Notifications panel.
 *
 * Per-user "Email Subscriptions" only — three opt-out toggles for
 * transactional notifications (ack-due reminders, new-assignment alerts,
 * weekly admin digest), plus a display-only Billing emails control that is
 * always-checked + disabled (billing emails are transactional and cannot be
 * opted out of). Writes via `UserService.updateMe`.
 *
 * The Digest Email Frequency control lived here in #1948 but moved to its own
 * workspace-only {@link DigestFrequencyPanel} in #2076 so non-admin users can
 * reach this panel without seeing tenant-wide settings they cannot change.
 *
 * Default behavior: when `user.notificationPreferences` is absent, all three
 * toggles render as **checked** (opt-out model — the user has to deliberately
 * silence a notification class, not deliberately subscribe). This matches the
 * server-side behavior where a missing preference is treated as "send."
 */
export default function NotificationsPanel() {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [ackDueReminders, setAckDueReminders] = useState<boolean>(DEFAULT_PREFERENCES.ackDueReminders);
    const [policyAssignments, setPolicyAssignments] = useState<boolean>(DEFAULT_PREFERENCES.policyAssignments);
    const [weeklyAdminDigest, setWeeklyAdminDigest] = useState<boolean>(DEFAULT_PREFERENCES.weeklyAdminDigest);
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [prefsError, setPrefsError] = useState<string | null>(null);
    const [prefsSuccess, setPrefsSuccess] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const meResult: User = await UserService.getMe();
                if (cancelled) return;
                const prefs = meResult.notificationPreferences;
                // Missing pref ⇒ opt-out default (`true`). Explicit `false`
                // wins, but we treat `undefined` the same as "subscribed."
                setAckDueReminders(prefs?.ackDueReminders ?? DEFAULT_PREFERENCES.ackDueReminders);
                setPolicyAssignments(prefs?.policyAssignments ?? DEFAULT_PREFERENCES.policyAssignments);
                setWeeklyAdminDigest(prefs?.weeklyAdminDigest ?? DEFAULT_PREFERENCES.weeklyAdminDigest);
            } catch {
                if (!cancelled) {
                    setLoadError('Failed to load notification settings. Please try again.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return <LoadingPage message="Loading notification settings..." />;
    }

    if (loadError) {
        return (
            <Alert severity="error" role="alert">
                {loadError}
            </Alert>
        );
    }

    const handlePrefsSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setPrefsError(null);
        setPrefsSuccess(false);
        setSavingPrefs(true);
        try {
            await UserService.updateMe({
                notificationPreferences: {
                    ackDueReminders,
                    policyAssignments,
                    weeklyAdminDigest,
                },
            });
            setPrefsSuccess(true);
            setTimeout(() => setPrefsSuccess(false), SUCCESS_AUTODISMISS_MS);
        } catch {
            setPrefsError('Failed to save preferences. Please try again.');
        } finally {
            setSavingPrefs(false);
        }
    };

    return (
        <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                    Email Subscriptions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose which notification emails you receive. Billing emails are required and cannot be turned off.
                </Typography>

                <Box component="form" onSubmit={handlePrefsSubmit}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={ackDueReminders}
                                    onChange={(event) => setAckDueReminders(event.target.checked)}
                                    disabled={savingPrefs}
                                    slotProps={{ input: { 'aria-label': 'Acknowledgment due reminders' } }}
                                />
                            }
                            label="Acknowledgment due reminders"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={policyAssignments}
                                    onChange={(event) => setPolicyAssignments(event.target.checked)}
                                    disabled={savingPrefs}
                                    slotProps={{ input: { 'aria-label': 'Policy assignment notifications' } }}
                                />
                            }
                            label="Policy assignment notifications"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={weeklyAdminDigest}
                                    onChange={(event) => setWeeklyAdminDigest(event.target.checked)}
                                    disabled={savingPrefs}
                                    slotProps={{ input: { 'aria-label': 'Weekly admin digest' } }}
                                />
                            }
                            label="Weekly admin digest"
                        />
                        <FormControlLabel
                            control={<Checkbox checked disabled slotProps={{ input: { 'aria-label': 'Billing emails' } }} />}
                            label="Billing emails"
                        />
                        <FormHelperText>Always sent — required for plan and payment notifications.</FormHelperText>
                    </FormGroup>

                    {prefsSuccess && (
                        <Alert severity="success" role="status" sx={{ mt: 2 }}>
                            Preferences saved
                        </Alert>
                    )}
                    {prefsError && (
                        <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                            {prefsError}
                        </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Button type="submit" variant="contained" disabled={savingPrefs}>
                            {savingPrefs ? 'Saving...' : 'Save preferences'}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Stack>
    );
}
